import { Link } from '@inertiajs/react'
import { useCallback, useEffect, useRef, useState } from 'react'

const ORDER_STEPS = [
  { key: 'created',   label: 'Placed' },
  { key: 'accepted',  label: 'Accepted' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'en_route',  label: 'En Route' },
  { key: 'arrived',   label: 'Arrived' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'settled',   label: 'Settled' },
]

const EVENT_LABELS = {
  queue_activated:   { label: 'Queue Activated',   icon: '🟢', color: 'text-green-700' },
  order_submitted:   { label: 'Order Submitted',   icon: '📋', color: 'text-blue-700' },
  preparing:         { label: 'Preparing',          icon: '🍳', color: 'text-yellow-700' },
  ready:             { label: 'Ready',              icon: '✅', color: 'text-green-700' },
  guest_eta_update:  { label: 'ETA Update',         icon: '🕐', color: 'text-gray-700' },
  guest_arrived:     { label: 'Guest Arrived',      icon: '📍', color: 'text-blue-700' },
  checkin_verified:  { label: 'Check-in Verified',  icon: '🔐', color: 'text-purple-700' },
  escrow_released:   { label: 'Escrow Released',    icon: '💸', color: 'text-green-700' },
  session_closed:    { label: 'Session Closed',     icon: '🏁', color: 'text-gray-700' }
}

const STATUS_COLORS = {
  created:    'bg-gray-100 text-gray-800',
  accepted:   'bg-blue-100 text-blue-800',
  preparing:  'bg-yellow-100 text-yellow-800',
  en_route:   'bg-orange-100 text-orange-800',
  arrived:    'bg-purple-100 text-purple-800',
  delivered:  'bg-green-100 text-green-800',
  cancelled:  'bg-red-100 text-red-800',
  settled:    'bg-green-100 text-green-800',
  held:       'bg-yellow-100 text-yellow-800'
}

const ESCROW_COLORS = {
  held:      'bg-yellow-100 text-yellow-800',
  released:  'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-700'
}

const API_BASE = '/api/v1/unified_api'
const CSRF = () => document.querySelector('[name="csrf-token"]')?.content

function SensepointPanel({ queue_session, viewer_role }) {
  const sessionId = queue_session?.id
  const mode = queue_session?.sensepoint_mode
  const sessionActive = queue_session?.session_status === 'active'

  const [requesting, setRequesting]         = useState(false)
  const [confirming, setConfirming]         = useState(false)
  const [handoffDone, setHandoffDone]       = useState(false)
  const [receipt, setReceipt]               = useState(null)
  const [error, setError]                   = useState(null)
  const [validationMethod, setValidationMethod] = useState('manual')
  const pollRef = useRef(null)

  // Poll messages for handoff_complete (guest side)
  const startPolling = useCallback(() => {
    if (pollRef.current) return
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/queue_sessions/${sessionId}/messages?role=${viewer_role}`,
          { headers: { 'X-CSRF-Token': CSRF() } }
        )
        const data = await res.json()
        const handoff = data.messages?.find(m => m.message_type === 'handoff_complete')
        if (handoff) {
          setHandoffDone(true)
          setReceipt(handoff.payload)
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch { /* non-fatal */ }
    }, 3000)
  }, [sessionId, viewer_role])

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  if (!sessionId || !sessionActive) return null

  // Guest panel
  if (viewer_role === 'guest') {
    if (handoffDone) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-green-800 mb-1">Check-in Complete</h2>
          <p className="text-green-700 text-sm mb-3">Your presence has been verified. Handoff complete.</p>
          {receipt?.receipt_id && (
            <p className="text-xs text-green-600 font-mono">Receipt: {receipt.receipt_id.slice(0, 16)}…</p>
          )}
        </div>
      )
    }
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-bold text-blue-800 mb-1">
          {mode === 'cloud' ? 'Automatic Check-in' : 'Mobile Check-in Required'}
        </h2>
        <p className="text-blue-700 text-sm mb-4">
          {mode === 'cloud'
            ? 'When you arrive, tap the button below — the server will verify your presence automatically.'
            : 'Tap the button below when you arrive. The creator\'s device will confirm your check-in.'}
        </p>
        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
        <button
          disabled={requesting}
          onClick={async () => {
            setRequesting(true)
            setError(null)
            try {
              const res = await fetch(
                `${API_BASE}/queue_sessions/${sessionId}/request_sensepoint`,
                { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': CSRF() } }
              )
              const data = await res.json()
              if (!res.ok) { setError(data.message || 'Request failed'); return }
              // Cloud mode returns receipt inline; mobile mode requires polling
              if (data.receipt) { setHandoffDone(true); setReceipt(data.receipt) }
              else { startPolling() }
            } catch { setError('Network error. Please try again.') }
            finally { setRequesting(false) }
          }}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold py-2 px-6 rounded-lg"
        >
          {requesting ? 'Sending…' : "I've Arrived"}
        </button>
      </div>
    )
  }

  // Creator panel (mobile mode only — cloud mode is automatic)
  if (viewer_role === 'creator' && mode === 'mobile') {
    if (handoffDone) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-green-800 mb-1">SensePoint Activated</h2>
          <p className="text-green-700 text-sm">Handoff complete. Guest has been notified.</p>
        </div>
      )
    }
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-bold text-purple-800 mb-1">Creator SensePoint</h2>
        <p className="text-purple-700 text-sm mb-4">
          Once the guest arrives, select a validation method and confirm their check-in.
        </p>
        <div className="flex gap-2 mb-4">
          {['manual', 'qr_scan', 'ble'].map(m => (
            <button
              key={m}
              onClick={() => setValidationMethod(m)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                validationMethod === m
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-100'
              }`}
            >
              {m === 'qr_scan' ? 'QR Scan' : m === 'ble' ? 'Bluetooth' : 'Manual'}
            </button>
          ))}
        </div>
        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
        <button
          disabled={confirming}
          onClick={async () => {
            setConfirming(true)
            setError(null)
            try {
              const res = await fetch(
                `${API_BASE}/queue_sessions/${sessionId}/activate_sensepoint`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': CSRF() },
                  body: JSON.stringify({ validation_method: validationMethod })
                }
              )
              const data = await res.json()
              if (!res.ok) { setError(data.message || 'Activation failed'); return }
              setHandoffDone(true)
              setReceipt(data.receipt)
            } catch { setError('Network error. Please try again.') }
            finally { setConfirming(false) }
          }}
          className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold py-2 px-6 rounded-lg"
        >
          {confirming ? 'Confirming…' : 'Confirm Check-in'}
        </button>
      </div>
    )
  }

  return null
}

export default function Show({ order, queue_session, timeline, viewer_role }) {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Nav */}
        <div className="flex items-center justify-between mb-8">
          <Link href="/" className="text-blue-600 hover:text-blue-800">← Offers</Link>
          <Link href="/wallet" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg">
            Wallet
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-6">Order Status</h1>

        {/* Order header */}
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Order #{order.id}</p>
              <p className="text-gray-700 mt-1 capitalize">
                {order.provider} · {order.category} · {order.delivery_mode || 'queue'}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-700'}`}>
                  {order.status}
                </span>
                {order.escrow_status && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ESCROW_COLORS[order.escrow_status] || 'bg-gray-100 text-gray-700'}`}>
                    Escrow: {order.escrow_status}
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Settlement:</span> {order.settlement_currency?.toUpperCase()}
                </div>
                {order.queue_session_id && (
                  <div>
                    <span className="font-medium">Session:</span> #{order.queue_session_id}
                  </div>
                )}
              </div>
            </div>

            {/* Status stepper */}
            {order.status === 'cancelled' ? (
              <div className="flex items-center">
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">Cancelled</span>
              </div>
            ) : (
              <ol className="flex flex-col gap-0 min-w-[110px]">
                {ORDER_STEPS.map((step, idx) => {
                  const currentIdx = ORDER_STEPS.findIndex(s => s.key === order.status)
                  const done   = currentIdx >= idx
                  const active = currentIdx === idx
                  return (
                    <li key={step.key} className="flex items-start gap-2">
                      <div className="flex flex-col items-center">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center text-[9px] font-bold
                          ${done ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 bg-white text-transparent'}`}>
                          ✓
                        </div>
                        {idx < ORDER_STEPS.length - 1 && (
                          <div className={`w-0.5 h-4 ${done && currentIdx > idx ? 'bg-green-500' : 'bg-gray-200'}`} />
                        )}
                      </div>
                      <span className={`text-xs pt-0.5 leading-tight
                        ${active ? 'text-green-700 font-bold' : done ? 'text-gray-600' : 'text-gray-300'}`}>
                        {step.label}
                      </span>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        </div>

        {/* SensePoint handoff panel */}
        <SensepointPanel queue_session={queue_session} viewer_role={viewer_role} />

        {/* Session status */}
        {queue_session && (
          <div className="bg-white rounded-xl shadow p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Queue Session</h2>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
              <span>
                <span className="font-medium">Status:</span>{' '}
                <span className="capitalize">{queue_session.session_status}</span>
              </span>
              {queue_session.sensepoint_mode && (
                <span>
                  <span className="font-medium">SensePoint:</span>{' '}
                  <span className="capitalize">{queue_session.sensepoint_mode}</span>
                </span>
              )}
              {queue_session.activated_at && (
                <span>
                  <span className="font-medium">Activated:</span>{' '}
                  {new Date(queue_session.activated_at).toLocaleTimeString()}
                </span>
              )}
              {queue_session.closed_at && (
                <span>
                  <span className="font-medium">Closed:</span>{' '}
                  {new Date(queue_session.closed_at).toLocaleTimeString()}
                  {queue_session.close_reason && ` (${queue_session.close_reason})`}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Event Timeline</h2>
          {timeline.length === 0 ? (
            <p className="text-gray-500 text-sm">No events yet.</p>
          ) : (
            <ol className="relative border-l border-gray-200 ml-3">
              {timeline.map((event) => {
                const meta = EVENT_LABELS[event.event_type] || { label: event.event_type, icon: '•', color: 'text-gray-700' }
                return (
                  <li key={event.id} className="mb-6 ml-6">
                    <span className="absolute -left-3 flex items-center justify-center w-6 h-6 bg-white border border-gray-200 rounded-full text-sm">
                      {meta.icon}
                    </span>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className={`font-semibold text-sm ${meta.color}`}>{meta.label}</p>
                      <span className="text-xs text-gray-400">
                        #{event.event_seq} · {new Date(event.occurred_at).toLocaleTimeString()}
                      </span>
                    </div>
                    {event.actor_role && (
                      <p className="text-xs text-gray-400 capitalize">by {event.actor_role}</p>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  )
}
